/**
 * Utility functions for saving and restoring admin page preferences
 * Saves filters, sort options, and page size to localStorage
 */

export interface PagePreferences {
  filters?: Record<string, any>;
  sort?: {
    column?: string;
    direction?: 'asc' | 'desc';
  };
  pageSize?: number;
  currentPage?: number;
}

/**
 * Get the storage key for a specific page
 */
function getStorageKey(pageName: string): string {
  return `admin-preferences-${pageName}`;
}

/**
 * Save preferences for a page
 */
export function savePreferences(pageName: string, preferences: PagePreferences): void {
  if (typeof window === 'undefined') return;
  
  try {
    const key = getStorageKey(pageName);
    localStorage.setItem(key, JSON.stringify(preferences));
  } catch (error) {
    console.error(`Failed to save preferences for ${pageName}:`, error);
  }
}

/**
 * Load preferences for a page
 */
export function loadPreferences(pageName: string): PagePreferences | null {
  if (typeof window === 'undefined') return null;
  
  try {
    const key = getStorageKey(pageName);
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored) as PagePreferences;
    }
  } catch (error) {
    console.error(`Failed to load preferences for ${pageName}:`, error);
  }
  
  return null;
}

/**
 * Clear preferences for a page
 */
export function clearPreferences(pageName: string): void {
  if (typeof window === 'undefined') return;
  
  try {
    const key = getStorageKey(pageName);
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Failed to clear preferences for ${pageName}:`, error);
  }
}

/**
 * Create a reactive preferences manager for a page
 * This watches changes and auto-saves them
 */
export function createPreferencesManager(pageName: string) {
  let currentPreferences: PagePreferences = {};
  
  // Load existing preferences
  const loaded = loadPreferences(pageName);
  if (loaded) {
    currentPreferences = loaded;
  }
  
  return {
    /**
     * Get current preferences
     */
    get(): PagePreferences {
      return { ...currentPreferences };
    },
    
    /**
     * Update preferences (merges with existing)
     */
    update(updates: Partial<PagePreferences>): void {
      currentPreferences = {
        ...currentPreferences,
        ...updates,
        filters: {
          ...currentPreferences.filters,
          ...updates.filters
        }
      };
      savePreferences(pageName, currentPreferences);
    },
    
    /**
     * Update filters specifically
     */
    updateFilters(filters: Record<string, any>): void {
      currentPreferences.filters = {
        ...currentPreferences.filters,
        ...filters
      };
      savePreferences(pageName, currentPreferences);
    },
    
    /**
     * Reset preferences to defaults
     */
    reset(): void {
      currentPreferences = {};
      clearPreferences(pageName);
    },
    
    /**
     * Get filters
     */
    getFilters(): Record<string, any> {
      return currentPreferences.filters || {};
    },
    
    /**
     * Get page size
     */
    getPageSize(): number | undefined {
      return currentPreferences.pageSize;
    },
    
    /**
     * Get current page
     */
    getCurrentPage(): number | undefined {
      return currentPreferences.currentPage;
    },
    
    /**
     * Get sort options
     */
    getSort(): { column?: string; direction?: 'asc' | 'desc' } | undefined {
      return currentPreferences.sort;
    }
  };
}

